export enum ErrorCategory {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTH',
  VALIDATION = 'VALIDATION',
  BUSINESS_LOGIC = 'BUSINESS',
  SYSTEM = 'SYSTEM',
  USER_CANCELLED = 'CANCELLED',
}

export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export interface AppError {
  category: ErrorCategory;
  severity: ErrorSeverity;
  message: string;
  userMessage: string;
  originalError?: Error;
  context?: Record<string, unknown>;
  timestamp: number;
  retryable: boolean;
}

export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'category' in error &&
    'severity' in error &&
    'message' in error &&
    'userMessage' in error
  );
}

export function isRetryable(category: ErrorCategory, message: string): boolean {
  if (category === ErrorCategory.NETWORK) return true;
  if (category === ErrorCategory.USER_CANCELLED) return false;
  if (category === ErrorCategory.VALIDATION) return false;
  const retryable = [/timeout/i, /ECONNREFUSED/i, /ETIMEDOUT/i, /ENOTFOUND/i, /socket hang up/i, /rate limit/i, /429/];
  return retryable.some((p) => p.test(message || ''));
}

export function getDefaultUserMessage(category: ErrorCategory, technicalMessage: string): string {
  switch (category) {
    case ErrorCategory.NETWORK:
      return 'Network connection issue. Please check your internet connection and try again.';
    case ErrorCategory.AUTHENTICATION:
      return 'Authentication failed. Please check your credentials.';
    case ErrorCategory.VALIDATION:
      return 'Invalid input. Please check your values and try again.';
    case ErrorCategory.BUSINESS_LOGIC:
      if ((technicalMessage || '').toLowerCase().includes('insufficient')) return 'Insufficient funds for this operation.';
      return 'This operation could not be completed. Please check the details and try again.';
    case ErrorCategory.USER_CANCELLED:
      return 'Operation cancelled.';
    case ErrorCategory.SYSTEM:
    default:
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}

export function createAppError(
  error: unknown,
  category: ErrorCategory,
  severity: ErrorSeverity,
  userMessage?: string,
  context?: Record<string, unknown>
): AppError {
  const originalError = error instanceof Error ? error : undefined;
  const message = originalError?.message || String(error);
  return {
    category,
    severity,
    message,
    userMessage: userMessage || getDefaultUserMessage(category, message),
    originalError,
    context,
    timestamp: Date.now(),
    retryable: isRetryable(category, message),
  };
}

export type SerializedAppError = Pick<AppError, 'category' | 'severity' | 'message' | 'userMessage' | 'timestamp' | 'retryable'> & {
  context?: Record<string, unknown>;
};

export function serializeAppError(err: AppError): SerializedAppError {
  return {
    category: err.category,
    severity: err.severity,
    message: err.message,
    userMessage: err.userMessage,
    timestamp: err.timestamp,
    retryable: err.retryable,
    context: err.context,
  };
}

export function deserializeAppError(s: SerializedAppError): AppError {
  return {
    category: s.category,
    severity: s.severity,
    message: s.message,
    userMessage: s.userMessage,
    originalError: undefined,
    context: s.context,
    timestamp: s.timestamp,
    retryable: s.retryable,
  };
}


